
import React, { useState } from "react";
import { IsNull } from "../utilities/util";
import { 
    X, 
    User, 
    CheckCircle2, 
    AlertCircle, 
    Settings, 
    Layers,
    MapPin,
    Calendar,
    Briefcase,
    GraduationCap,
    Heart
} from "lucide-react";

interface ComparisonMatrimonialProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    profile1: any;
    profile2: any;
    preference1: any;
    preference2: any;
    sammelanRegistrations1?: any[];
    sammelanRegistrations2?: any[];
}

const ComparisonMatrimonialProfileModal: React.FC<ComparisonMatrimonialProfileModalProps> = ({
    isOpen,
    onClose,
    profile1,
    profile2,
    preference1,
    preference2,
    sammelanRegistrations1 = [],
    sammelanRegistrations2 = [],
}) => {
    const [activeTab, setActiveTab] = useState<"profile" | "preference">("profile");

    if (!isOpen) return null;

    /* Helper to get normalized value from data object supporting different casings */
    const getValue = (data: any, key: string): any => {
        if (!data) return undefined;
        
        // 1. Try exact match
        if (data[key] !== undefined) return data[key];
    
        // 2. Try snake_case (assuming key is PascalCase or camelCase)
        // e.g. BirthTime -> birth_time, familyIncome -> family_income
        const snakeKey = key.replace(/([A-Z])/g, "_$1").toLowerCase().replace(/^_/, "");
        if (data[snakeKey] !== undefined) return data[snakeKey];
    
        // 3. Try lowercase
        const lowerKey = key.toLowerCase();
        if (data[lowerKey] !== undefined) return data[lowerKey];

        // 4. Try nested lookups if it's a preference object
        const categories = ['lifestyle', 'personal', 'additional', 'preferences', 'education', 'career'];
        for (const cat of categories) {
            if (data[cat] && typeof data[cat] === 'object') {
                const inner = getValue(data[cat], key);
                if (inner !== undefined) return inner;
            }
        }
        
        return undefined;
    };

    const renderValue = (val: any) => {
        if (IsNull(val)) return <span className="text-slate-600 italic">-</span>;
        
        if (Array.isArray(val)) {
            if (val.length === 0) return <span className="text-slate-600 italic">None</span>;
            
            // Check if it's the children array
            if (val.length > 0 && (val[0]?.name || val[0]?.Name || val[0]?.age)) {
                return (
                    <div className="flex flex-col gap-1">
                        {val.map((child, i) => (
                            <div key={i} className="text-[10px] bg-white/5 px-2 py-1 rounded">
                                {child.name || child.Name || "Child"} ({child.age || child.Age || "?"}y)
                            </div>
                        ))}
                    </div>
                );
            }
            return val.join(", ");
        }

        if (typeof val === 'boolean') {
            return val ? "Yes" : "No";
        }

        if (typeof val === 'object' && val !== null) {
            if ('value' in val) return val.value + (val.negotiable ? " (Neg.)" : "");
            if ('name' in val) return val.name + (val.negotiable ? " (Neg.)" : "");
            if ('min' in val && 'max' in val) return `${val.min} - ${val.max}` + (val.negotiable ? " (Neg.)" : "");
            if ('preferred_location' in val) return val.preferred_location + (val.negotiable ? " (Neg.)" : "");
            
            // Handle other nested objects
            const keys = Object.keys(val).filter(k => typeof val[k] !== 'object');
            if (keys.length > 0) {
                return keys.map(k => `${k}: ${val[k]}`).join(", ");
            }
            
            return <span className="text-slate-700 text-xs">Complex Data</span>;
        }
        return String(val);
    };

    const isMatch = (val1: any, val2: any) => {
        if (IsNull(val1) || IsNull(val2)) return false;
        
        // Handle array comparison (e.g. counts or presence)
        if (Array.isArray(val1) && Array.isArray(val2)) {
            return val1.length === val2.length;
        }

        // Normalize values for comparison
        const v1 = typeof val1 === 'object' && val1?.value ? val1.value : val1;
        const v2 = typeof val2 === 'object' && val2?.value ? val2.value : val2;

        const s1 = String(v1).toLowerCase().trim();
        const s2 = String(v2).toLowerCase().trim();
        return s1 === s2 && s1 !== "not disclosed" && s1 !== "-" && s1 !== "false" && s1 !== "no";
    };

    const getIconForKey = (key: string) => {
        const k = key.toLowerCase();
        if (k.includes('age') || k.includes('birth')) return <Calendar size={14} />;
        if (k.includes('city') || k.includes('state') || k.includes('location')) return <MapPin size={14} />;
        if (k.includes('occupation') || k.includes('company')) return <Briefcase size={14} />;
        if (k.includes('qualification') || k.includes('education')) return <GraduationCap size={14} />;
        if (k.includes('status')) return <Heart size={14} />;
        return <Layers size={14} />;
    };

    // Helper to get all unique keys from both objects recursively if needed, 
    // but for comparison we usually want a flattened list of "interesting" keys.
    // Let's use a standard list + any extra keys found in the objects.
    const getAllKeys = (obj1: any, obj2: any) => {
        const keys = new Set<string>();
        
        // Helper to add keys from an object
        const addKeys = (o: any) => {
            if (!o) return;
            Object.keys(o).forEach(k => {
                // If the value is a nested category object (like lifestyle), add its children keys instead of itself
                if (['lifestyle', 'personal', 'additional', 'preferences'].includes(k.toLowerCase()) && typeof o[k] === 'object') {
                    Object.keys(o[k]).forEach(subK => keys.add(subK));
                } else if (!['lifestyle', 'personal', 'additional', 'preferences'].includes(k.toLowerCase())) {
                    keys.add(k);
                }
            });
        };

        addKeys(obj1);
        addKeys(obj2);

        // Filter out metadata
        const ignored = ['isactive', 'createdat', 'updatedat', 'photo', 'userid', 'candidate_id', 'name', 'id', 'profile', 'is_profile', 'user_id'];
        return Array.from(keys).filter(k => !ignored.includes(k.toLowerCase().replace(/_/g, '')));
    };

    const data1 = activeTab === "profile" ? (profile1 || {}) : (preference1 || {});
    const data2 = activeTab === "profile" ? (profile2 || {}) : (preference2 || {});
    
    // Flatten keys to compare
    const comparisonKeys = getAllKeys(data1, data2);

    return (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300" onClick={onClose} />
            
            <div className="relative bg-[#0a0c10] border border-slate-800 rounded-[2.5rem] shadow-[0_0_100px_rgba(0,0,0,0.8)] w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
                {/* Header Section */}
                <div className="p-8 pb-4 shrink-0 border-b border-slate-800/50 bg-gradient-to-b from-slate-900/50 to-transparent">
                    <div className="flex justify-between items-start mb-8">
                        <div>
                            <h2 className="text-2xl font-black text-white flex items-center gap-3">
                                <Layers className="text-indigo-500" />
                                <span className="tracking-tight uppercase">Comparison</span>
                            </h2>
                        </div>
                        <button 
                            onClick={onClose}
                            className="p-3 bg-slate-800/50 hover:bg-rose-500/20 hover:text-rose-500 rounded-full transition-all text-slate-400 border border-slate-700/50"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className="grid grid-cols-11 gap-6 mb-6">
                        {/* Registration Badges Profile 1 */}
                        <div className="col-span-4 flex justify-end">
                            <div className="flex flex-wrap justify-end gap-1.5 max-w-[200px]">
                                {sammelanRegistrations1?.map((reg: any, i: number) => (
                                    <div key={i} className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-tighter border ${reg.is_registered ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-500 border-rose-500/20 opacity-30 shadow-none'}`}>
                                        {reg.event_name}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Spacer */}
                        <div className="col-span-3" />

                        {/* Registration Badges Profile 2 */}
                        <div className="col-span-4 flex justify-start">
                            <div className="flex flex-wrap justify-start gap-1.5 max-w-[200px]">
                                {sammelanRegistrations2?.map((reg: any, i: number) => (
                                    <div key={i} className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-tighter border ${reg.is_registered ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-500 border-rose-500/20 opacity-30 shadow-none'}`}>
                                        {reg.event_name}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-11 items-center gap-6 mb-4">
                        {/* User Header */}
                        <div className="col-span-4 flex flex-col items-center gap-3">
                            <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 overflow-hidden shadow-lg shadow-indigo-500/10">
                                {profile1?.photo ? (
                                    <img src={profile1.photo} className="w-full h-full object-cover" alt="User" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-indigo-400 font-bold text-xl">{profile1?.name?.[0]}</div>
                                )}
                            </div>
                            <div className="text-center">
                                <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest block mb-1">User</span>
                                <h3 className="text-sm font-black text-white truncate max-w-[150px]">{profile1?.name || 'User'}</h3>
                            </div>
                        </div>

                        {/* VS Divider */}
                        <div className="col-span-3 flex flex-col items-center">
                            <div className="px-5 py-1.5 bg-slate-800/80 border border-slate-700 rounded-full text-[10px] font-black text-slate-400 tracking-[0.3em] uppercase mb-4">VS</div>
                            <div className="w-px h-12 bg-gradient-to-b from-slate-700 to-transparent"></div>
                        </div>

                        {/* Candidate Header */}
                        <div className="col-span-4 flex flex-col items-center gap-3">
                            <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 overflow-hidden shadow-lg shadow-emerald-500/10">
                                {profile2?.photo ? (
                                    <img src={profile2.photo} className="w-full h-full object-cover" alt="Candidate" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-emerald-400 font-bold text-xl">{profile2?.candidate_id?.[0] || 'C'}</div>
                                )}
                            </div>
                            <div className="text-center">
                                <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest block mb-1">Candidate</span>
                                <h3 className="text-sm font-black text-white truncate max-w-[150px]">{profile2?.name || profile2?.candidate_id || 'Candidate'}</h3>
                            </div>
                        </div>
                    </div>

                    {/* Navigation Tabs */}
                    <div className="flex gap-2 p-1 bg-slate-950 rounded-2xl border border-slate-800 w-fit mt-4">
                        {[
                            { id: 'profile', label: 'Profile Details', icon: <User size={14} /> },
                            { id: 'preference', label: 'Preferences', icon: <Settings size={14} /> }
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`px-5 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2 uppercase tracking-tight ${
                                    activeTab === tab.id
                                        ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/20"
                                        : "text-slate-500 hover:text-slate-300"
                                }`}
                            >
                                {tab.icon}
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content Section */}
                <div className="flex-1 overflow-y-auto p-8 pt-4 custom-scrollbar">
                    <div className="space-y-3 pb-8">
                        {comparisonKeys.map((key) => {
                            const value1 = getValue(data1, key);
                            const value2 = getValue(data2, key);
                            // Skip if both empty
                            if ((value1 === undefined || value1 === null) && (value2 === undefined || value2 === null)) return null;

                            const matched = isMatch(value1, value2);

                            return (
                                <div key={key} className="group relative">
                                    <div className={`grid grid-cols-11 gap-6 items-center p-4 rounded-2xl border transition-all ${
                                        matched ? 'bg-emerald-950/20 border-emerald-500/20' : 'bg-slate-900/40 border-white/[0.02] hover:bg-white/[0.04]'
                                    }`}>
                                        {/* Profile 1 Value */}
                                        <div className="col-span-4 text-right">
                                            <div className={`text-xs font-bold transition-colors ${matched ? 'text-emerald-400' : 'text-slate-300'}`}>
                                                {renderValue(value1)}
                                            </div>
                                        </div>

                                        {/* Key / Field Name */}
                                        <div className="col-span-3">
                                            <div className="flex flex-col items-center gap-1.5 px-3 py-2 bg-slate-950/50 rounded-xl border border-white/[0.03]">
                                                <span className="text-slate-600">{getIconForKey(key)}</span>
                                                <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest text-center leading-tight">
                                                    {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                                                </span>
                                                {matched && (
                                                    <div className="absolute top-0 transform -translate-y-1/2">
                                                        <CheckCircle2 size={16} className="text-emerald-500 fill-slate-950" />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Profile 2 Value */}
                                        <div className="col-span-4 text-left">
                                            <div className={`text-xs font-bold transition-colors ${matched ? 'text-emerald-400' : 'text-slate-300'}`}>
                                                {renderValue(value2)}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Visual Connection Line */}
                                    {matched && (
                                        <div className="absolute inset-0 border border-emerald-500/20 rounded-2xl pointer-events-none opacity-100"></div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    
                    {/* Empty State */}
                    {comparisonKeys.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-20 opacity-20 capitalize">
                            <AlertCircle size={48} className="mb-4" />
                            <p className="font-black tracking-widest uppercase text-xs">Insufficient comparison data detected</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ComparisonMatrimonialProfileModal;
